import type { EmodeMarketCategory, Market, MarketUserState, Reserve } from '@aave/graphql';
import { UserReserveData } from '@aave/math-utils';
import { client } from 'pages/_app.page';
import React, { PropsWithChildren, useContext } from 'react';
import { EmodeCategory } from 'src/helpers/types';
import { useWeb3Context } from 'src/libs/hooks/useWeb3Context';
import { useRootStore } from 'src/store/root';
import { fetchIconSymbolAndName } from 'src/ui-config/reservePatches';
import { SWARD_ONLY } from 'src/utils/marketsAndNetworksConfig';

import { formatEmodes } from '../../store/poolSelectors';
import {
  ExtendedFormattedUser as _ExtendedFormattedUser,
  useExtendedUserSummaryAndIncentives,
} from '../pool/useExtendedUserSummaryAndIncentives';
import {
  FormattedReservesAndIncentives,
  usePoolFormattedReserves,
} from '../pool/usePoolFormattedReserves';
import { usePoolReservesHumanized } from '../pool/usePoolReserves';
import { useUserPoolReservesHumanized } from '../pool/useUserPoolReserves';
import { FormattedUserReserves } from '../pool/useUserSummaryAndIncentives';
import { useMarketsData } from './useMarketsData';

/**
 * removes the marketPrefix from a symbol
 * @param symbol
 * @param prefix
 */
export const unPrefixSymbol = (symbol: string, prefix: string) => {
  return symbol.toUpperCase().replace(RegExp(`^(${prefix[0]}?${prefix.slice(1)})`), '');
};

/**
 * @deprecated Use FormattedReservesAndIncentives type from usePoolFormattedReserves hook
 */
export type ComputedReserveData = FormattedReservesAndIncentives;

/**
 * @deprecated Use FormattedUserReserves type from useUserSummaryAndIncentives hook
 */
export type ComputedUserReserveData = FormattedUserReserves;

/**
 * @deprecated Use ExtendedFormattedUser type from useExtendedUserSummaryAndIncentives hook
 */
export type ExtendedFormattedUser = _ExtendedFormattedUser;
export type ReserveWithId = Reserve & { id: string };
export interface AppDataContextType {
  loading: boolean;
  /** SDK market snapshot */
  market?: Market;
  totalBorrows?: number;
  supplyReserves: ReserveWithId[];
  borrowReserves: ReserveWithId[];
  eModeCategories: EmodeMarketCategory[];
  userState?: MarketUserState;
  /** Legacy fields (deprecated) kept temporarily for incremental migration */
  reserves: ComputedReserveData[];
  eModes: Record<number, EmodeCategory>;
  user?: ExtendedFormattedUser;
  marketReferencePriceInUsd: string;
  marketReferenceCurrencyDecimals: number;
  userReserves: UserReserveData[];
}

const AppDataContext = React.createContext<AppDataContextType>({} as AppDataContextType);

/**
 * This is the only provider you'll ever need.
 * It fetches reserves /incentives & walletbalances & keeps them updated.
 */
export const AppDataProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { currentAccount } = useWeb3Context();

  const currentMarketData = useRootStore((state) => state.currentMarketData);

  const { data, isPending } = useMarketsData({
    client,
    marketData: currentMarketData,
    account: currentAccount,
  });

  const marketAddress = currentMarketData.addresses.LENDING_POOL.toLowerCase();

  const sdkMarket = data?.find((item) => item.address.toLowerCase() === marketAddress);

  const sdkTotalBorrows = sdkMarket?.borrowReserves.reduce((acc, reserve) => {
    const value = reserve.borrowInfo?.total?.usd ?? 0;
    return acc + Number(value);
  }, 0);

  const patchUnderlyingToken = (reserve: Reserve) => {
    const patch = fetchIconSymbolAndName({
      underlyingAsset: reserve.underlyingToken.address,
      symbol: reserve.underlyingToken.symbol,
      name: reserve.underlyingToken.name,
    });
    return {
      ...reserve.underlyingToken,
      name: patch.name ?? reserve.underlyingToken.name,
      symbol: patch.symbol ?? reserve.underlyingToken.symbol,
    };
  };

  const sdkSupplyReserves = (sdkMarket?.supplyReserves ?? []).map((reserve) => ({
    ...reserve,
    underlyingToken: patchUnderlyingToken(reserve),
    id: `${sdkMarket?.address}-${reserve.underlyingToken.address}`,
  }));

  const sdkBorrowReserves = (sdkMarket?.borrowReserves ?? []).map((reserve) => ({
    ...reserve,
    underlyingToken: patchUnderlyingToken(reserve),
    id: `${sdkMarket?.address}-${reserve.underlyingToken.address}`,
  }));

  const eModeCategories = sdkMarket?.eModeCategories ?? [];
  const marketUserState = sdkMarket?.userState ?? undefined;

  const { data: reservesData, isPending: reservesDataLoading } =
    usePoolReservesHumanized(currentMarketData);
  const { data: formattedPoolReserves, isPending: formattedPoolReservesLoading } =
    usePoolFormattedReserves(currentMarketData);
  const baseCurrencyData = reservesData?.baseCurrencyData;

  // Aave's hosted GraphQL API cannot contain custom deployments. For Sward,
  // adapt the same on-chain reserve data already used by the dashboard into the
  // newer market-page shape introduced by the current interface.
  const swardSupplyReserves = (formattedPoolReserves ?? []).map(
    (reserve) => {
      const amountToUsd = (amount: string) =>
        String(Number(amount || 0) * Number(reserve.priceInUSD || 0));

      return ({
        id: reserve.id,
        underlyingToken: {
          address: reserve.underlyingAsset,
          name: reserve.name,
          symbol: reserve.symbol,
          decimals: reserve.decimals,
        },
        aToken: { address: reserve.aTokenAddress },
        vToken: { address: reserve.variableDebtTokenAddress },
        size: {
          amount: { value: reserve.totalLiquidity },
          usd: reserve.totalLiquidityUSD,
        },
        supplyInfo: {
          total: { value: reserve.totalLiquidity },
          apy: { value: reserve.supplyAPY },
          supplyCap: {
            amount: { value: reserve.supplyCap },
            usd: amountToUsd(reserve.supplyCap),
          },
          supplyCapReached:
            Number(reserve.supplyCap) > 0 &&
            Number(reserve.totalLiquidity) >= Number(reserve.supplyCap),
          maxLTV: { value: reserve.formattedBaseLTVasCollateral },
          liquidationThreshold: { value: reserve.formattedReserveLiquidationThreshold },
          liquidationBonus: { value: reserve.formattedReserveLiquidationBonus },
        },
        borrowInfo: {
          total: {
            amount: { value: reserve.totalDebt },
            usd: reserve.totalDebtUSD,
          },
          apy: { value: reserve.variableBorrowAPY },
          borrowingState: reserve.borrowingEnabled ? 'ENABLED' : 'DISABLED',
          borrowCap: {
            amount: { value: reserve.borrowCap },
            usd: amountToUsd(reserve.borrowCap),
          },
          borrowCapReached:
            Number(reserve.borrowCap) > 0 && Number(reserve.totalDebt) >= Number(reserve.borrowCap),
          utilizationRate: { value: reserve.borrowUsageRatio },
          reserveFactor: { value: String(Number(reserve.reserveFactor) / 10_000) },
          baseVariableBorrowRate: { raw: reserve.baseVariableBorrowRate },
          optimalUsageRate: { raw: reserve.optimalUsageRatio },
          variableRateSlope1: { raw: reserve.variableRateSlope1 },
          variableRateSlope2: { raw: reserve.variableRateSlope2 },
        },
        incentives: [],
        isFrozen: reserve.isFrozen,
        isPaused: reserve.isPaused,
        acceptsNative: reserve.isWrappedBaseAsset,
        isolationModeConfig: {
          canBeCollateral: reserve.isIsolated,
          totalBorrows: {
            amount: { value: reserve.isolationModeTotalDebt },
            usd: reserve.isolationModeTotalDebtUSD,
          },
          debtCeiling: {
            amount: { value: reserve.debtCeiling },
            usd: reserve.debtCeilingUSD,
          },
        },
        eModeInfo: [],
        interestRateStrategyAddress: reserve.interestRateStrategyAddress,
      } as unknown as ReserveWithId);
    }
  );

  const supplyReserves = SWARD_ONLY ? swardSupplyReserves : sdkSupplyReserves;
  const borrowReserves = SWARD_ONLY ? swardSupplyReserves : sdkBorrowReserves;
  const totalBorrows = SWARD_ONLY
    ? (formattedPoolReserves ?? []).reduce((sum, reserve) => sum + Number(reserve.totalDebtUSD), 0)
    : sdkTotalBorrows;
  const market = SWARD_ONLY
    ? ({
        address: marketAddress,
        totalMarketSize: (formattedPoolReserves ?? []).reduce(
          (sum, reserve) => sum + Number(reserve.totalLiquidityUSD),
          0
        ),
        totalAvailableLiquidity: (formattedPoolReserves ?? []).reduce(
          (sum, reserve) => sum + Number(reserve.availableLiquidityUSD),
          0
        ),
      } as unknown as Market)
    : sdkMarket;
  // user hooks

  const eModes = formattedPoolReserves ? formatEmodes(formattedPoolReserves) : {};

  const { data: userReservesData, isPending: userReservesDataLoading } =
    useUserPoolReservesHumanized(currentMarketData);
  const { data: userSummary, isPending: userSummaryLoading } =
    useExtendedUserSummaryAndIncentives(currentMarketData);
  const userReserves = userReservesData?.userReserves;

  // loading
  const isReservesLoading = reservesDataLoading || formattedPoolReservesLoading;
  const isUserDataLoading = userReservesDataLoading || userSummaryLoading;

  const loading =
    (!SWARD_ONLY && isPending) || isReservesLoading || (!!currentAccount && isUserDataLoading);

  return (
    <AppDataContext.Provider
      value={{
        loading,
        market,
        totalBorrows,
        supplyReserves,
        borrowReserves,
        eModeCategories,
        userState: marketUserState,
        // Legacy fields (to be removed once consumers migrate)
        reserves: formattedPoolReserves || [],
        eModes,
        user: userSummary,
        userReserves: userReserves || [],
        marketReferencePriceInUsd: baseCurrencyData?.marketReferenceCurrencyPriceInUsd || '0',
        marketReferenceCurrencyDecimals: baseCurrencyData?.marketReferenceCurrencyDecimals || 0,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};

export const useAppDataContext = () => useContext(AppDataContext);
